(ns dashboard.views.rooms
  (:require
   [reagent.core :as reagent]
   [clojure.string :refer [split lower-case blank?]]
   [re-frame.core :as re-frame]
   [breaking-point.core :as bp]
   [headlessui-reagent.core :as ui]
   [dashboard.i18n :refer [tr]]
   [dashboard.subs :as subs]
   [dashboard.subs.event-organizer :as event-organizer-subs]
   [dashboard.utils :refer
    [listen cs index-by href download-as-xlsx ->json json-> isOff
     format-time-in-timezone vec-remove format-time hex->rgba]]
   [dashboard.events.bookings :as booking-events]
   [dashboard.components.inputs :refer [textarea input]]
   [dashboard.components.button :refer [button dropdown-button switch-buttons]]
   [dashboard.components.tooltip :refer [tooltip]]
   [dashboard.components.select :refer
    [multiselect select space-select month-select time-select day-select]]
   [dashboard.components.drawer :refer [drawer]]
   [dashboard.components.tag :refer [tag]]
   [dashboard.components.profile :refer [profile-img display-user-fullname]]
   [inflections.core :refer [plural]]
   ["date-fns" :refer
    [getDaysInMonth setDate format formatISO addMonths subMonths isSameDay
     isToday isBefore eachDayOfInterval setYear setMonth endOfMonth startOfWeek
     endOfWeek startOfMonth getMonth parseISO getYear parse formatDistance isDate]]
   ["date-fns/locale" :as date-fns-locale]
   ["@heroicons/react/solid" :refer
    [ChevronLeftIcon ChevronRightIcon ChevronDownIcon DownloadIcon ExclamationIcon
     CalendarIcon InformationCircleIcon LocationMarkerIcon XIcon CheckIcon UsersIcon]]
   ["@deck.gl/react" :refer [DeckGL]]
   ["@deck.gl/core" :refer [OrthographicView COORDINATE_SYSTEM]]
   ["@deck.gl/layers" :refer [BitmapLayer GeoJsonLayer TextLayer]]
   ["@turf/center" :default center]))

(def room-statuses
  {:room.status/free {:intent "success"
                      :color "#35831B"}
   :room.status/busy {:intent "danger"
                      :color "#D50033"}
   :room.status/cleaning-needed {:intent "yellow-500"
                                 :color "#eab308"}
   :room.status/cleaned {:intent "gray-500"
                         :color "#6b7280"}})

(defn- comment-tooltip [{:keys [comment]}]
  [tooltip
   [:div.mt-2.max-w-2xl.whitespace-normal
    comment]])

(defn- guests-tooltip [{:keys [guests]}]
  [tooltip
   (doall
    (for [guest guests]
      ^{:key (gensym)}
      [:div.flex.px-2
       [:span.font-semibold
        (:name guest)]
       [:span.ml-4.text-gray-600
        (:gender guest)]
       (when (some? (:age guest))
         [:span.ml-2.text-gray-600
          (str (:age guest) "歳")])]))])

(defn- progress
  [{:keys [class steps]}]
  (let [all-checked? (->> steps
                          (map #(some? (:ts %)))
                          (every? identity))]
    [:nav
     {:aria-label "Progress"
      :class class}
     [:ol.flex.items-center
      {:role "list"}
      (doall
       (for [step steps]
         ^{:key (:id step)}
         [:li.flex-1.text-center
          [:span.text-xs.text-gray-500.whitespace-nowrap
           (:name step)]]))]
     [:ol.flex.items-center.px-6
      {:role "list"}
      (doall
       (for [step steps]
         ^{:key (:id step)}
         [:li.relative
          {:class (cs ""
                      (when (not= (:id step) (:id (last steps)))
                        "pr-4 sm:pr-12"))}
          [:<>
           [:div.absolute.inset-0.flex.items-center
            {:aria-hidden true}
            [:div.w-full
             {:class (cs "h-0.5"
                         (if (and (:checked? step) (not (:current? step)))
                           (if all-checked?
                             "bg-gray-400"
                             "bg-primary-600")
                           "bg-gray-200"))}]]
           [:a.relative.flex.h-6.w-6.items-center.justify-center.rounded-full.cursor-pointer
            {:on-click (:on-click step)
             :aria-current (when (:current? step) "step")
             :class (cs ""
                        (if (:checked? step)
                          (if all-checked?
                            "bg-gray-400 hover:bg-gray-700"
                            "bg-primary-600 hover:bg-primary-900")
                          "border-2 border-gray-300 bg-white hover:border-gray-400"))}
            (if (:current? step)
              [:span.rounded-full.bg-white
               {:class "h-2 w-2"
                :aria-hidden true}]
              (if (:checked? step)
                [:> CheckIcon
                 {:class "h-4 w-4 text-white"}]
                [:span.rounded-full.bg-transparent.group-hover:bg-gray-300
                 {:class "h-2.5 w-2.5"
                  :aria-hidden true}]))
            [:span.sr-only
             (:id step)]]]]))]
     [:ol.flex.items-center
      {:role "list"}
      (doall
       (for [step steps]
         ^{:key (:id step)}
         [:li.flex-1.text-center
          [:span.text-xs.text-gray-500
           (when (some? (:ts step))
             (format-time (js/Date. (:ts step))))]]))]]))

(defn- assign-booking-drawer-content [{:keys [state]}]
  (let [selected-date-str (format (:date @state) "yyyy-MM-dd")
        current-bookings (->> (get (listen ::event-organizer-subs/bookings-by-date) selected-date-str))
        bookings-by-start-time (->> current-bookings
                                    (sort-by :startTime)
                                    (group-by :startTime))
        booking-times (->> current-bookings
                           (map :startTime)
                           (distinct)
                           (sort))
        start-time (:start-time @state)
        bookings (get bookings-by-start-time start-time)]
    [:div.mt-6.flex-1
     [:nav.flex-1.min-h-0.overflow-y-auto
      [:div
       [:div.z-10.sticky.top-0.border-t.border-b.border-gray-300.bg-gray-100.px-4.py-1.text-sm.font-medium.text-gray-500
        start-time]
       [:ul.z-0.divide-y.divide-gray-200
        {:role "list"}
        (doall
         (for [booking bookings
               :let [booking-room (when (some? (:roomId booking))
                                    (get (listen ::event-organizer-subs/rooms-by-id) (:roomId booking)))
                     not-assigned? (and
                                    (some? (:checkIn booking))
                                    (not (some? (:roomId booking))))]]
           ^{:key (str "assign-" (:id booking))}
           [:li.relative.px-2.py-4.flex.items-center.space-x-3.focus:outline-none.focus-within:ring-2.focus-within:ring-inset.focus-within:ring-primary-500
            {:class (cs ""
                        (if (some? (:checkIn booking))
                          "bg-white hover:bg-gray-100 cursor-pointer"
                          "bg-gray-50 cursor-not-allowed"))
             :on-click #(when (some? (:checkIn booking))
                          (re-frame/dispatch [::booking-events/update-booking
                                              {:booking (assoc booking
                                                               :roomId (:id (:room @state))
                                                               :enter (listen ::subs/time))}]))}
            [:div.flex-shrink-0
             [:span.inline-flex.items-center.justify-center.rounded-full.bg-gray-200.font-semibold.h-8.w-8.text-sm
              {:class (cs ""
                          (if not-assigned?
                            "text-primary-500"
                            "text-gray-900"))}
              (or (:externalId booking) "?")]]
            [:div.flex-1.min-w-0
             [:div.focus:outline-none
              [:span.absolute.inset-0
               {:aria-hidden true}]
              [:p.text-sm.font-medium.flex.whitespace-nowrap
               {:class (cs ""
                           (if not-assigned?
                             "text-primary-500"
                             "text-gray-900"))}
               (str (or (:guestLastNameJp booking)
                        (:guestLastName booking))
                    " "
                    (or (:guestFirstNameJp booking)
                        (:guestFirstName booking)))
               (when
                   (and
                    (some? (:guestNumbers booking))
                    (> (:guestNumbers booking) 1))
                 [:span.ml-2.text-gray-500
                  (str (:guestNumbers booking) "人")])]
              (when (and
                     (some? (:guestLastNameJp booking))
                     (some? (:guestLastName booking)))
                [:p.text-xs.truncate.-mt-1
                 {:class (cs ""
                             (if not-assigned?
                               "text-primary-500"
                               "text-gray-500"))}
                 (str (:guestLastName booking) " " (:guestFirstName booking))])
              [:p.text-sm
               (:roomType booking)]
              [:p.text-xs.truncate.flex.space-x-2
               [:span.text-gray-700
                (:boutiqueName booking)]
               [:span.text-gray-500
                (:inviterName booking)]]]]
            [:div.items-center.w-24.text-center
             (if (some? (:cancel booking))
               [:span.text-red-600.text-xs
                (tr :booking/cancelled)]
               (if (some? (:checkIn booking))
                 (if (some? booking-room)
                   [tag
                    {:color (:color booking-room)}
                    (:name booking-room)]
                   [:p.text-xs.flex.flex-col.text-center
                    [:span.text-gray-900
                     (str
                      (tr :booking.status/arrived)
                      (formatDistance (:checkIn booking) (js/Date.)
                                      #js {:addSuffix true
                                           :locale (listen ::subs/date-fns-locale)}))]])
                 [:span.text-gray-600.text-xs
                  (tr :booking/not-checked-in)]))]]))]]]]))

(defn- booking-drawer-content [{:keys [state]}]
  [:div.relative.mt-6.flex-1

   [:p.text-sm
    (:productName (:booking @state))]

   [:div.space-y-1.sm:grid.sm:grid-cols-3.sm:gap-2.sm:space-y-0.mt-3
    [:div
     [:label.block.text-sm.font-medium.text-gray-900.sm:mt-px.sm:pt-2
      [:div.flex
       (tr :booking.status/arrived-time)]]]
    [:div.sm:col-span-2.flex.space-x-2
     [time-select
      {:class "w-24"
       :disabled? true
       :start-time "10:00"
       :end-time "20:40"
       :step-minutes 10
       :selected {:id (when (isDate (get-in @state [:booking :checkIn]))
                        (format (get-in @state [:booking :checkIn]) "HH:mm"))
                  :name (when (isDate (get-in @state [:booking :checkIn]))
                          (format (get-in @state [:booking :checkIn]) "HH:mm"))}
       :on-change #(do
                     (swap! state assoc-in [:booking :checkIn] (parse % "HH:mm" (:date @state))))}]]]

   [:div.space-y-1.sm:grid.sm:grid-cols-3.sm:gap-2.sm:space-y-0.mt-3
    [:div
     [:label.block.text-sm.font-medium.text-gray-900.sm:mt-px.sm:pt-2
      [:div.flex
       (tr :booking.status/enter-time)]]]
    [:div.sm:col-span-2.flex.space-x-2
     [time-select
      {:class "w-24"
       :disabled? (= (:action @state) :view)
       :start-time "10:00"
       :end-time "20:40"
       :step-minutes 10
       :selected {:id (when (isDate (get-in @state [:booking :enter]))
                        (format (get-in @state [:booking :enter]) "HH:mm"))
                  :name (when (isDate (get-in @state [:booking :enter]))
                          (format (get-in @state [:booking :enter]) "HH:mm"))}
       :on-change #(do
                     (re-frame/dispatch [::booking-events/update-booking
                                         {:booking (assoc (:booking @state)
                                                          :enter (parse % "HH:mm" (:date @state)))}])
                     (swap! state assoc-in [:booking :enter]
                            (parse % "HH:mm" (get-in @state [:booking :start]))))}]
     [:button
      {:on-click #(do
                    (re-frame/dispatch [::booking-events/update-booking
                                        {:booking (assoc (:booking @state)
                                                         :enter nil
                                                         :roomId nil)}])
                    (swap! state assoc-in [:booking :enter] nil))}
      [:> XIcon
       {:class "h-5 w-5 text-danger"}]]]]

   [:div.space-y-1.sm:grid.sm:grid-cols-3.sm:gap-2.sm:space-y-0.mt-3
    [:div
     [:label.block.text-sm.font-medium.text-gray-900.sm:mt-px.sm:pt-2
      [:div.flex
       (tr :booking.status/exit-time)]]]
    [:div.sm:col-span-2.flex.space-x-2
     [time-select
      {:class "w-24"
       :disabled? (= (:action @state) :view)
       :start-time "10:00"
       :end-time "20:40"
       :step-minutes 10
       :selected {:id (when (isDate (get-in @state [:booking :exit]))
                        (format (get-in @state [:booking :exit]) "HH:mm"))
                  :name (when (isDate (get-in @state [:booking :exit]))
                          (format (get-in @state [:booking :exit]) "HH:mm"))}
       :on-change #(do
                     (re-frame/dispatch [::booking-events/update-booking
                                         {:booking (assoc (:booking @state)
                                                          :exit (parse % "HH:mm" (:date @state)))}])
                     (swap! state assoc-in [:booking :exit]
                            (parse % "HH:mm" (get-in @state [:booking :start]))))}]
     [:button
      {:on-click #(do
                    (re-frame/dispatch [::booking-events/update-booking
                                        {:booking (assoc (:booking @state) :exit nil)}])
                    (swap! state assoc-in [:booking :exit] nil))}
      [:> XIcon
       {:class "h-5 w-5 text-danger"}]]]]

   [:div.space-y-1.sm:grid.sm:grid-cols-3.sm:gap-2.sm:space-y-0.mt-3
    [:div
     [:label.block.text-sm.font-medium.text-gray-900.sm:mt-px.sm:pt-2
      [:div.flex
       (tr :booking.status/cleaned-time)]]]
    [:div.sm:col-span-2.flex.space-x-2
     [time-select
      {:class "w-24"
       :disabled? (= (:action @state) :view)
       :start-time "10:00"
       :end-time "20:40"
       :step-minutes 10
       :selected {:id (when (isDate (get-in @state [:booking :clean]))
                        (format (get-in @state [:booking :clean]) "HH:mm"))
                  :name (when (isDate (get-in @state [:booking :clean]))
                          (format (get-in @state [:booking :clean]) "HH:mm"))}
       :on-change #(do
                     (re-frame/dispatch [::booking-events/update-booking
                                         {:booking (assoc (:booking @state)
                                                          :clean (parse % "HH:mm" (:date @state)))}])
                     (swap! state assoc-in [:booking :clean]
                            (parse % "HH:mm" (get-in @state [:booking :start]))))}]
     [:button
      {:on-click #(do
                    (re-frame/dispatch [::booking-events/update-booking
                                        {:booking (assoc (:booking @state) :clean nil)}])
                    (swap! state assoc-in [:booking :clean] nil))}
      [:> XIcon
       {:class "h-5 w-5 text-danger"}]]]]])

(defn- floorplan-map [{:keys [day]}]
  (let [selected-date-str (format @day "yyyy-MM-dd")
        current-bookings (->> (get (listen ::event-organizer-subs/bookings-by-date) selected-date-str)
                              (remove #(nil? (:roomId %)))
                              (sort-by :startTime))
        time-by-room-id (->> (group-by :roomId current-bookings)
                             (map (fn [[k v]]
                                    (let [booking (last v)]
                                      {k (:startTime booking)})))
                             (apply merge))
        status-by-room-id (->> (group-by :roomId current-bookings)
                               (map (fn [[k v]]
                                      (let [booking (last v)]
                                        {k (cond
                                             (or (nil? booking)
                                                 (nil? (:checkIn booking))
                                                 (some? (:clean booking)))
                                             :room.status/free

                                             (and
                                              (some? (:exit booking))
                                              (nil? (:clean booking)))
                                             :room.status/cleaning-needed

                                             (and
                                              (some? (:exit booking))
                                              (some? (:clean booking)))
                                             :room.status/cleaned

                                             (and
                                              (some? (:checkIn booking))
                                              (nil? (:checkOut booking)))
                                             :room.status/busy

                                             :else nil)})))
                               (apply merge))]
    [:div.flex.relative
     [:div.h-128.w-128
      [:> DeckGL
       {:views (OrthographicView.
                #js {:id "basemap"
                     :flipY false})
        :initialViewState (clj->js (:initialViewState (listen ::event-organizer-subs/floorplan-config)))
        :coordinateSystem (.-CARTESIAN COORDINATE_SYSTEM)
        :controller true
        :onViewStateChange #(js/console.log (aget % "viewState"))
        :onClick #(js/console.log (aget % "coordinate"))
        :layers [(BitmapLayer.
                  #js {:id "event-map-layer"
                       :bounds (clj->js (:bounds (listen ::event-organizer-subs/floorplan-config)))
                       :image (listen ::event-organizer-subs/floorplan-url)
                       :pickable true})
                 (GeoJsonLayer.
                  #js {:id "room-geometry-layer"
                       :data (->> (listen ::event-organizer-subs/rooms)
                                  (remove #(nil? (:geometry %)))
                                  (map #(hash-map :type "Feature"
                                                  :geometry (:geometry %)
                                                  :properties {:id (:id %)}))
                                  (clj->js))
                       :getLineColor (fn [room]
                                       (let [status (get status-by-room-id (aget room "properties" "id") :room.status/free)
                                             color (:color (get room-statuses status))]
                                         (clj->js (hex->rgba color 255))))
                       :getFillColor (fn [room]
                                       (let [status (get status-by-room-id (aget room "properties" "id") :room.status/free)
                                             color (:color (get room-statuses status))]
                                         (clj->js (hex->rgba color 100))))
                       :filled true
                       :pickable true})
                 (TextLayer.
                  #js {:id "room-name-layer"
                       :data (->> (listen ::event-organizer-subs/rooms)
                                  (remove #(nil? (:geometry %)))
                                  (map #(hash-map :name (:nameJp %)
                                                  :id (:id %)
                                                  :center (center (clj->js
                                                                   {:type "Feature"
                                                                    :geometry (:geometry %)}))))
                                  (clj->js))
                       :getPosition #(aget % "center" "geometry" "coordinates")
                       :getText (fn [room]
                                  (let [status (get status-by-room-id (aget room "id") :room.status/free)
                                        start-time (get time-by-room-id (aget room "id"))]
                                    (str (aget room "name")
                                         (when (not= status :room.status/free)
                                           (str "\n" start-time)))))
                       :fontFamily "Helvetica"
                       :getSize 14
                       :getAngle 0
                       :getTextAnchor "middle"})]}]
      [:div.absolute.bottom-0.left-0.ml-4.mb-4.bg-white.border.shadow.p-4
       [:div.flex.flex-col
        (doall
         (for [[room-status-id room-status] room-statuses]
           ^{:key room-status-id}
           [:span.flex
            [:div.inline-block.h-4.w-4.border
             {:style {:background-color (:color room-status)}}]
            [:span.ml-2
             (tr room-status-id)]]))]]]]))

(defn content []
  (let [!booking-drawer-state (reagent/atom {:is-open? false
                                             :booking nil
                                             :date nil})
        !assign-booking-drawer-state (reagent/atom {:is-open? false
                                                    :room nil
                                                    :date nil})
        !collapsed-state (reagent/atom {})
        !selected-date (reagent/atom (listen ::event-organizer-subs/default-selected-date))
        !show-map? (reagent/atom false)]
    (fn []
      (let [selected-date-str (format @!selected-date "yyyy-MM-dd")
            current-bookings (get (listen ::event-organizer-subs/bookings-by-date) selected-date-str)
            bookings-by-start-time (group-by :startTime current-bookings)
            booking-times (->> current-bookings
                               (map :startTime)
                               (distinct)
                               (sort))]
        [:div.bg-white.rounded-lg.shadow.py-6.min-h-full
         [drawer
          {:state !assign-booking-drawer-state
           :title (str (:nameJp (:room @!assign-booking-drawer-state)) "に" (tr :action/assign-fn (tr :booking)))
           :content [assign-booking-drawer-content
                     {:state !assign-booking-drawer-state}]}]
         [drawer
          {:state !booking-drawer-state
           :title (str
                   (:startTime (:booking @!booking-drawer-state))
                   " - "
                   (or
                    (:guestLastNameJp (:booking @!booking-drawer-state))
                    (:guestLastName (:booking @!booking-drawer-state)))
                   "　"
                   (or
                    (:guestFirstNameJp (:booking @!booking-drawer-state))
                    (:guestFirstName (:booking @!booking-drawer-state))))
           :content [booking-drawer-content
                     {:state !booking-drawer-state}]}]
         [:header.sm:flex.flex-none.items-center.sm:justify-between.py-2.sm:py-4.px-2.sm:px-4.px-5..sm:px-6
          [:div.flex.justify-between.sm:justify-left.items-center
           [day-select
            {:day !selected-date}]
           (when (isSameDay @!selected-date (listen ::subs/time))
             [:h1.text-2xl.text-primary-500.ml-3
              (format (listen ::subs/time) "HH:mm")])]
          [:div.flex.space-x-2.justify-between.sm:justify-right.mt-2.sm:mt-0
           [switch-buttons
            {:content-left "Map"
             :content-right "Grid"
             :active-side (if @!show-map? :left :right)
             :on-click-left #(reset! !show-map? true)
             :on-click-right #(reset! !show-map? false)}]]]

         (if @!show-map?
           [floorplan-map
            {:day !selected-date}]
           ;; Rooms grid
           (doall
            (for [[previous-booking-time booking-time] (partition 2 1 (cons nil booking-times))
                  :let [bookings (get bookings-by-start-time booking-time)
                        entered-bookings (->> bookings
                                              (filter #(some? (:roomId %))))
                        previous-bookings (get bookings-by-start-time previous-booking-time)
                        bookings-by-room (index-by bookings :roomId)
                        previous-bookings-by-room (index-by previous-bookings :roomId)]]
              ^{:key (str "bookings-by-time-" booking-time)}
              [:div.pt-3.pb-5.px-5.sm:px-6.odd:bg-gray-50.even:bg-gray-100
               [:div.flex.items-center
                [:button.border.rounded-md.shadow-sm.inline-flex.justify-center.text-sm.font-medium.focus:outline-none.focus:ring-2.focus:ring-offset-2
                 {:type "button"
                  :class (cs "bg-white hover:bg-gray-50 focus:ring-red-500 text-gray-700 border-gray-300")
                  :on-click #(swap! !collapsed-state assoc booking-time (not (get @!collapsed-state booking-time true)))}
                 (if (get @!collapsed-state booking-time true)
                   [:> ChevronDownIcon
                    {:class "h-6 w-6 text-gray-500"}]
                   [:> ChevronRightIcon
                    {:class "h-6 w-6 text-gray-500"}])]
                [:h2.text-gray-700.text-2xl.ml-2.flex
                 booking-time
                 [:span.text-gray-400.ml-3
                  [:span.text-xs
                   "予約 "]
                  [:span.text-base.font-semibold
                   (count bookings)]
                  [:span.text-xs
                   "件・部屋割当済 "]
                  [:span.text-base.font-semibold
                   (count entered-bookings)]
                  [:span.text-xs
                   "件 "]]]]
               ;; Bookings for the given slot
               (when (get @!collapsed-state booking-time true)
                 (doall
                  (for [[room-type rooms] (->> (listen ::event-organizer-subs/rooms)
                                               (sort-by :name)
                                               (group-by :type)
                                               (reverse))]
                    ^{:key (str (or room-type (gensym)) "-group")}
                    [:<>
                     (when (some? room-type)
                       [:h3.text-gray-500.text-xl.flex.mt-3
                        room-type])
                     [:ul.mt-3.grid.grid-cols-1.gap-6.sm:grid-cols-2.lg:grid-cols-3
                      {:role "list"}
                      (doall
                       (for [room rooms
                             :let [booking (get bookings-by-room (:id room))
                                   previous-booking (get previous-bookings-by-room (:id room))
                                   room-status-id (cond
                                                    (and
                                                     (or (nil? booking)
                                                         (nil? (:checkIn booking)))
                                                     (or (nil? previous-booking)
                                                         (some? (:clean previous-booking))))
                                                    :room.status/free

                                                    (or
                                                     (and
                                                      (some? (:exit booking))
                                                      (nil? (:clean booking)))
                                                     (and
                                                      (some? previous-booking)
                                                      (some? (:exit previous-booking))
                                                      (nil? (:clean previous-booking))))
                                                    :room.status/cleaning-needed

                                                    (and
                                                     (some? (:exit booking))
                                                     (some? (:clean booking)))
                                                    :room.status/cleaned

                                                    (or
                                                     (and
                                                      (some? (:checkIn booking))
                                                      (nil? (:checkOut booking)))
                                                     (and
                                                      (some? previous-booking)
                                                      (nil? (:exit previous-booking))))
                                                    :room.status/busy

                                                    :else :room.status/free)
                                   room-status (get room-statuses room-status-id)]]
                         ^{:key (str "time-" booking-time "-room-" (:id room))}
                         [:li.col-span-1.shadow.divide-y.divide-gray-200
                          {:class (cs (str "border-l-8 border-" (:color room) "-500")
                                      (if (not= room-status-id :room.status/free)
                                        "bg-gray-50"
                                        "bg-white"))}
                          [:div.w-full.flex.items-center.justify-between.p-3.space-x-6
                           [:div.flex-1.truncate
                            ;; Header
                            [:div.flex.justify-between
                             [:div.flex.items-center.space-x-3
                              [:h3.text-gray-900.font-semibold.truncate
                               [:span.flex-shrink-0.inline-block.text-gray-800.font-semibold
                                {:class "py-0.5"}
                                (:name room)]]]
                             [:div
                              (when (some? room-status)
                                [tag
                                 {:intent (:intent room-status)}
                                 (tr room-status-id)])]]

                            ;; Content
                            (if (not= room-status-id :room.status/free)
                              (if (nil? booking)
                                [:div.flex.items-center.justify-center.h-32.text-gray-700
                                 (cond
                                   (nil? (:exit previous-booking))
                                   (tr :room/previous-booking-still-ongoing)

                                   (and
                                    (some? (:exit previous-booking))
                                    (nil? (:clean previous-booking)))
                                   (tr :room/cleaning-needed-from-previous-booking)

                                   :else "")]
                                [:div.mt-1.text-gray-500.text-sm.truncate.h-32
                                 [:div.flex
                                  [:dt.text-sm.font-medium.text-gray-500.w-20.flex-none
                                   "顧客様"]
                                  [:dd.text-sm.text-gray-900.h-4.flex-1.justify-between.flex
                                   [:div.flex
                                    [:span.text-primary-500
                                     (str
                                      (or
                                       (:guestLastNameJp booking)
                                       (:guestLastName booking)) "　"
                                      (or
                                       (:guestFirstNameJp booking)
                                       (:guestFirstName booking)))]
                                    (when (and
                                           (some? (:guestNumbers booking))
                                           (> (:guestNumbers booking) 1))
                                      [:span.ml-2
                                       (str (:guestNumbers booking) "人")])
                                    (when (and (> (:guestNumbers booking) 1)
                                               (not (empty? (:accompanyingGuests booking))))
                                      [:span.relative
                                       [:div.group
                                        [guests-tooltip
                                         {:guests (:accompanyingGuests booking)}]
                                        [:> UsersIcon
                                         {:class "h-4 w-4 ml-1 mt-0.5 text-gray-600"}]]])]
                                   [:div.text-xs.text-gray-500
                                    (:roomType booking)]]]
                                 [:div.flex
                                  [:dt.text-sm.font-medium.text-gray-500.w-20
                                   (tr :booking/inviter)]
                                  [:dd.text-sm.text-gray-900.h-4.flex.space-x-3
                                   (:inviterName booking)]]
                                 [:div.flex
                                  [:dt.text-sm.font-medium.text-gray-500.w-20
                                   (tr :booking/boutiqueName)]
                                  [:dd.text-sm.text-gray-900.h-4
                                   (:boutiqueName booking)]]
                                 [:div.mt-2.min-w-0.px-6
                                  [progress
                                   {:steps [{:id (str (:id booking) "-check-in")
                                             :name (tr :booking.status/check-in)
                                             :ts (:checkIn booking)
                                             :current? (and
                                                        (some? (:checkIn booking))
                                                        (not (some? (:enter booking))))
                                             :checked? (some? (:checkIn booking))}
                                            {:id (str (:id booking) "-enter")
                                             :name (tr :booking.status/assigned)
                                             :on-click #(reset! !booking-drawer-state {:is-open? true
                                                                                       :booking booking
                                                                                       :date @!selected-date})
                                             :ts (:enter booking)
                                             :current? (and
                                                        (some? (:enter booking))
                                                        (not (some? (:exit booking))))
                                             :checked? (some? (:enter booking))}
                                            {:id (str (:id booking) "-exit")
                                             :name (tr :booking.status/exit)
                                             :ts (:exit booking)
                                             :on-click #(do
                                                          (when (and
                                                                 (not (nil? (:enter booking)))
                                                                 (nil? (:clean booking)))
                                                            (re-frame/dispatch [::booking-events/update-booking
                                                                                {:booking (assoc booking
                                                                                                 :exit (listen ::subs/time))}]))
                                                          (reset! !booking-drawer-state {:is-open? true
                                                                                         :booking booking
                                                                                         :date @!selected-date}))
                                             :current? (and
                                                        (some? (:exit booking))
                                                        (not (some? (:clean booking))))
                                             :checked? (some? (:exit booking))}
                                            {:id (str (:id booking) "-cleaning")
                                             :name (tr :booking.status/cleaned)
                                             :ts (:clean booking)
                                             :on-click #(do
                                                          (when (and
                                                                 (not (nil? (:exit booking)))
                                                                 (nil? (:clean booking)))
                                                            (re-frame/dispatch [::booking-events/update-booking
                                                                                {:booking (assoc booking
                                                                                                 :clean (listen ::subs/time))}]))
                                                          (reset! !booking-drawer-state {:is-open? true
                                                                                         :booking booking
                                                                                         :date @!selected-date}))
                                             :checked? (some? (:clean booking))}]}]]])
                              ;; Assign room button
                              [:div.flex.items-center.justify-center.h-32
                               [button
                                {:on-click #(reset! !assign-booking-drawer-state {:is-open? true
                                                                                  :room room
                                                                                  :start-time booking-time
                                                                                  :date @!selected-date})}
                                (tr :action/assign-fn (tr :booking))]])]
                           #_[:span.inline-flex.items-center.px-3.rounded-full.text-sm.font-medium.text-white
                              {:class (cs ""
                                          (if (= (:status room) :free)
                                            "bg-success"
                                            "bg-danger"))}
                              (tr (keyword :room.status (:status room)))]]

                          ;; Footer
                          #_[:div
                             (when (some? booking)
                               [:div.-mt-px.flex.divide-x.divide-gray-200
                                [:div.w-0.flex-1.flex
                                 [:a.relative.-mr-px.w-0.flex-1.inline-flex.items-center.justify-center.py-4.text-sm.text-gray-700.font-medium.border.border-transparent.rounded-bl-lg.hover:text-gray-500
                                  {:on-click #(reset! !assign-booking-drawer-state {:is-open? true
                                                                                    :booking booking
                                                                                    :date @!selected-date})}
                                  (tr :action/edit)]]
                                [:div.w-0.flex-1.flex
                                 [:a.relative.-mr-px.w-0.flex-1.inline-flex.items-center.justify-center.py-4.text-sm.font-medium.border.border-transparent.rounded-bl-lg.hover:text-gray-500
                                  {:class (cs ""
                                              (if (= room-status-id :room.status/cleaned)
                                                "text-gray-400" "text-gray-700"))}
                                  (cond
                                    (nil? booking)
                                    "割当"

                                    (= room-status-id :room.status/cleaning-needed)
                                    "掃除必要"

                                    (= room-status-id :room.status/cleaned)
                                    (tr :room.status/cleaned)

                                    (= room-status-id :room.status/free)
                                    "チェックイン"

                                    (= room-status-id :room.status/busy)
                                    "チェックアウト"

                                    :else room-status-id)]]])]]))]])))])))]))))
